/* eslint-disable react/prop-types */
import React, { useEffect, useState, Component, ErrorInfo, ReactNode } from 'react';
import { Offcanvas } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format, parse } from 'date-fns';
import { RootState, AppDispatch } from 'src/slices/store';
import { closePanel } from 'src/slices/Compare/reducer';
import CompareSelectionScreen from './CompareSelectionScreen';
import CompareResultsScreen from './CompareResultsScreen';
import './compare.css';

interface ErrorBoundaryProps { children: ReactNode; }

class ComparePanelErrorBoundary extends Component<ErrorBoundaryProps, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ComparePanel error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="compare-no-data">
          Something went wrong. Please close and reopen the panel.
        </div>
      );
    }
    return this.props.children;
  }
}

const ComparePanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // viewMode is ephemeral UI state — not stored in Redux
  const [viewMode, setViewMode] = useState<'sidebar' | 'fullpage'>('sidebar');

  const openExchange = useSelector((state: RootState) => state.compare.openExchange);
  const exState = useSelector((state: RootState) =>
    openExchange ? state.compare.exchanges[openExchange] : null
  );

  const isOpen  = openExchange !== null;
  const screen  = exState?.screen ?? 'selection';
  const error   = exState?.error ?? null;

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    document.documentElement.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    dispatch(closePanel());
    setViewMode('sidebar');
  };

  const formatDateStr = (dateStr: string) =>
    format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'MMM dd, yyyy');

  const expandIcon = viewMode === 'sidebar'
    ? <i className="mdi mdi-arrow-expand-horizontal" />
    : <i className="mdi mdi-arrow-collapse-horizontal" />;

  const content = (
    <>
      {/* Header */}
      <div className="compare-panel-header">
        <div>
          <h5 className="compare-panel-title">
            Compare Line Item Data
            {openExchange && (
              <span className="font-size-12 text-muted fw-normal ms-2">— {openExchange}</span>
            )}
          </h5>
          {screen === 'results' && exState?.dateRange1 && exState?.dateRange2 && (
            <div className="text-muted small mt-1">
              Comparing: {formatDateStr(exState.dateRange1.start)} – {formatDateStr(exState.dateRange1.end)} vs {formatDateStr(exState.dateRange2.start)} – {formatDateStr(exState.dateRange2.end)}
            </div>
          )}
        </div>
        <div className="compare-panel-actions">
          {screen === 'results' && (
            <button
              type="button"
              className="compare-expand-btn"
              onClick={() => setViewMode(v => v === 'sidebar' ? 'fullpage' : 'sidebar')}
              title={viewMode === 'sidebar' ? 'Expand' : 'Collapse'}
            >
              {expandIcon}
            </button>
          )}
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
            aria-label="Close"
          />
        </div>
      </div>

      {/* Body */}
      <div className="compare-panel-body">
        <ComparePanelErrorBoundary>
          {screen === 'selection'
            ? <CompareSelectionScreen />
            : (
              <CompareResultsScreen
                viewMode={viewMode}
                onCollapseViewMode={() => setViewMode('sidebar')}
              />
            )
          }
        </ComparePanelErrorBoundary>
      </div>
    </>
  );

  return (
    <>
      {/* Backdrop — closes sidebar on click, blocks interaction in fullpage */}
      {isOpen && (
        <div
          className="compare-backdrop"
          onClick={viewMode === 'sidebar' ? handleClose : undefined}
        />
      )}

      {/* Fullpage overlay — rendered on top of the Offcanvas, removed on collapse */}
      {isOpen && viewMode === 'fullpage' && (
        <div className="compare-fullpage-overlay">
          {content}
        </div>
      )}

      {/* Offcanvas stays mounted — hidden when fullpage to avoid re-animation on collapse */}
      <Offcanvas
        isOpen={isOpen}
        direction="end"
        backdrop={false}
        className={`compare-panel${viewMode === 'fullpage' ? ' compare-panel--hidden' : ''}`}
      >
        {content}
      </Offcanvas>
    </>
  );
};

export default ComparePanel;
